import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Section } from "../components/shared/Section";
import { SEO } from "../components/shared/SEO";

export default function MusicVideoProductionAssam() {

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Music Video Production in Assam",
    "description": "Professional music video production services across Assam and Northeast India.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Hydrox Studios",
      "areaServed": {
        "@type": "Place",
        "name": "Assam, India"
      }
    }
  };

  return (
    <div className="text-white">
      <SEO
        title="Music Video Production in Assam | Professional Video Studio Northeast India"
        description="Hydrox Studios offers professional music video production services across Assam. Cinematic filming, editing and post-production for artists in Northeast India."
        path="/music-video-production-assam"
        schema={blogSchema}
      />

      {/* Hero */}
      <Section className="pt-32 pb-20 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-syne text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-6 max-w-4xl mx-auto"
        >
          Music Video Production in Assam
        </motion.h1>

        <p className="text-muted-foreground max-w-3xl mx-auto text-lg">
          Professional cinematic music video production services for artists
          across Assam and Northeast India.
        </p>
      </Section>

      {/* Content */}
      <Section>
        <div className="max-w-4xl mx-auto text-muted-foreground space-y-8 leading-relaxed">

          <h2 className="text-2xl font-syne font-semibold text-white">
            Serving Artists Across Assam
          </h2>

          <p>
            While Hydrox Studios is based in Guwahati, we provide music video
            production services across Assam. From urban city shoots to
            location-based productions in surrounding districts, our team
            manages structured filming and post-production workflows.
          </p>

          <p>
            Professional music video production in Assam requires planning,
            equipment logistics and coordinated scheduling. Structured
            execution ensures smooth project delivery.
          </p>

          <h2 className="text-2xl font-syne font-semibold text-white">
            Our Assam Music Video Services Include
          </h2>

          <ul className="list-disc list-inside space-y-3">
            <li>Concept development & creative direction</li>
            <li>Location scouting across Assam</li>
            <li>Professional filming & lighting setups</li>
            <li>Music video editing & color grading</li>
            <li>VFX & advanced post-production</li>
          </ul>

          <h2 className="text-2xl font-syne font-semibold text-white">
            Why Work With a Guwahati-Based Production Studio?
          </h2>

          <p>
            Being centrally located in Guwahati allows efficient coordination
            across Assam. Equipment management, crew scheduling and post-
            production facilities are organized from a structured base.
          </p>

          <p>
            Learn more about our{" "}
            <Link
              to="/music-video-production-guwahati"
              className="text-primary underline"
            >
              Music Video Production in Guwahati
            </Link>{" "}
            and how we extend services across the region.
          </p>

          <h2 className="text-2xl font-syne font-semibold text-white">
            Planning a Music Video in Assam?
          </h2>

          <p>
            Whether you are an independent artist or label, structured
            production ensures clarity in budgeting, timelines and final
            output quality.
          </p>

        </div>
      </Section>

      {/* CTA */}
      <Section className="text-center pb-24">
        <h2 className="font-syne text-3xl md:text-4xl font-bold mb-6">
          Start Your Music Video Project in Assam
        </h2>

        <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
          Contact Hydrox Studios to discuss your concept and production scope.
        </p>

        <Link to="/contact">
          <Button size="lg" className="px-8 py-6">
            Get a Quote
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </Link>
      </Section>
    </div>
  );
}